import {graphql, PageProps} from 'gatsby';
import * as React from 'react';
import {useTranslation} from 'react-i18next';

import CTA from '@src/components/CTA';
import GridOneCol from '@src/components/Grid/GridOneCol';
import Hero from '@src/components/Hero';
import PageHero from '@src/components/Hero/PageHero';
import PageLayout, {getTranslations} from '@src/components/PageLayout';
import PageSection from '@src/components/PageSection';
import PageTheme from '@src/components/PageTheme';
import Redirect from '@src/components/Redirect';
import {canEnroll} from '@src/utils';

import AgentPortalUserWarning from './AgentPortalUserWarning';
import Body from './Body';
import * as styles from './enroll.module.scss';
import QualificationWarning from './QualificationWarning';

import type {EnrollQuery} from '@graphql-types';

interface EnrollComponentProps {
  pbp: EnrollQuery['allPbps']['nodes'][number];
}

const EnrollHeader: React.FC<EnrollComponentProps> = ({pbp}) => {
  const {t} = useTranslation('EnrollTemplate');

  return (
    <Hero theme="fade">
      <PageHero>
        <h1>{t('pageHeading', {planName: `${pbp.name} for ${pbp.year}`})}</h1>
      </PageHero>
    </Hero>
  );
};

const Buttons: React.FC<EnrollComponentProps> = ({pbp}) => {
  const {t} = useTranslation('EnrollTemplate');

  return (
    <div className={styles.buttons}>
      <ul className={styles.buttonList}>
        <li className={styles.buttonItem}>
          <CTA
            theme="primary"
            size="large"
            text={t('enroll')}
            link={`${t('enrollLink', {year: pbp.year})}?${pbp.record_locator}`}
          />
        </li>
      </ul>
    </div>
  );
};

const EnrollTemplate = ({
  data: {
    allLanguages: {nodes: languages},
    allPbps,
    allPrismicMainNavigation: navigation,
    allPrismicNotifications: notifications,
    site: {
      siteMetadata: {lastBuildTime, siteName, siteUrl},
    },
  },
  location,
  pageContext: {pages},
}: PageProps<
  EnrollQuery,
  {
    pages: Array<{
      lang: string;
      path: string;
    }>;
    recordLocator: string;
  }
>) => {
  // assume single result
  const pbp = allPbps.nodes[0];

  if (!canEnroll(new Date(), parseInt(pbp.year, 10))) {
    return <Redirect to="/too-early-to-enroll/" />;
  }

  return (
    <PageLayout
      {...PageLayout.dateProps(lastBuildTime)}
      languages={languages}
      location={location}
      navigation={navigation}
      notifications={notifications}
      siteName={siteName}
      siteUrl={siteUrl}
      translations={getTranslations(pages, languages)}>
      <PageTheme theme="white">
        <EnrollHeader pbp={pbp} />
        <PageSection>
          <GridOneCol gridType="small">
            <AgentPortalUserWarning />
            <QualificationWarning pbp={pbp} />
            <Buttons pbp={pbp} />
            <Body />
            <Buttons pbp={pbp} />
          </GridOneCol>
          <GridOneCol gridType="small" />
        </PageSection>
      </PageTheme>
    </PageLayout>
  );
};

export default EnrollTemplate;

export const query = graphql`
  query Enroll($recordLocator: String!) {
    ...PageLayout

    allPbps(filter: {record_locator: {eq: $recordLocator}}) {
      nodes {
        name
        plan {
          ma_variant
        }
        record_locator
        year
      }
    }
  }
`;
